/**
 * Centralized API Configuration Service
 * 
 * This service provides a single source of truth for all API base URLs
 * and environment detection throughout the application.
 */

class APIConfig {
  private static instance: APIConfig | null = null
  private _isProduction: boolean | null = null
  private _apiBaseUrl: string | null = null
  private _configUrl: string | null = null
  private _swimBaseUrl: string | null = null

  private constructor() {}

  static getInstance(): APIConfig {
    if (!APIConfig.instance) {
      APIConfig.instance = new APIConfig()
    }
    return APIConfig.instance
  }

  /**
   * Determine if we're in production environment
   * Uses hostname-based detection for reliability across different deployment scenarios
   */
  get isProduction(): boolean {
    if (this._isProduction === null) {
      // Production is only when served from the production frontend domain
      this._isProduction = window.location.hostname === 'wital.ai' ||
                           window.location.hostname === 'www.wital.ai'

      if (import.meta.env.DEV) {
        console.log('🔧 APIConfig: Hostname:', window.location.hostname)
        console.log('🔧 APIConfig: Is production:', this._isProduction)
      }
    }
    return this._isProduction
  }

  /**
   * Get the base URL for API calls
   * Production / dev.wital.ai: same-origin (relative URLs via /api/*)
   * iOS native: absolute URL to the appropriate backend
   * Local dev: dev backend
   */
  get apiBaseUrl(): string {
    if (this._apiBaseUrl === null) {
      const host = window.location.hostname
      if (this.isProduction || host === 'dev.wital.ai') {
        // Same-origin: CloudFront/nginx routes /api/* to WITAL backend
        this._apiBaseUrl = ''
      } else if (host === 'localhost') {
        // Local development
        this._apiBaseUrl = 'https://dev.wital.ai'
      } else {
        this._apiBaseUrl = ''
      }
      if (import.meta.env.DEV) console.log('🔧 APIConfig: API Base URL:', this._apiBaseUrl)
    }
    return this._apiBaseUrl
  }

  /**
   * Get the full URL for config endpoint
   */
  get configUrl(): string {
    if (this._configUrl === null) {
      this._configUrl = this.buildUrl('/api/config')
      if (import.meta.env.DEV) console.log('🔧 APIConfig: Config URL:', this._configUrl)
    }
    return this._configUrl
  }

  /**
   * Get the health check URL (for ConnectionMonitor)
   */
  get healthUrl(): string {
    return this.buildUrl('/api/health')
  }

  /**
   * Direct SWIM base URL for endpoints not yet migrated to WITAL backend
   * (email/telegram notifications — will move in Phase 3).
   */
  get swimBaseUrl(): string {
    if (this._swimBaseUrl === null) {
      const host = window.location.hostname
      if (this.isProduction) {
        this._swimBaseUrl = 'https://sciapis.com'
      } else if (host === 'dev.wital.ai') {
        this._swimBaseUrl = 'https://dev.sciapis.com'
      } else {
        this._swimBaseUrl = 'https://dev.sciapis.com'
      }
    }
    return this._swimBaseUrl
  }

  /**
   * Build a full SWIM URL for endpoints not yet migrated.
   */
  buildSwimUrl(endpoint: string): string {
    return `${this.swimBaseUrl}${endpoint}`
  }

  /**
   * Build a full API URL by combining base URL with endpoint
   */
  buildUrl(endpoint: string): string {
    if (endpoint.startsWith('/')) {
      return `${this.apiBaseUrl}${endpoint}`
    }
    return `${this.apiBaseUrl}/${endpoint}`
  }

  /**
   * Clear cached values (useful for testing)
   */
  clearCache(): void {
    this._isProduction = null
    this._apiBaseUrl = null
    this._configUrl = null
  }
}

// Export singleton instance
export const apiConfig = APIConfig.getInstance()

// Export class for testing if needed
export { APIConfig } 